// src/features/system/devices/components/iot/StreamerManagerDialog.tsx
// ────────────────────────────────────────────────────────────────────────────
// Telemetry Streamer Manager — PACDaemon CLI-spirit indexed multi-select.
//
// Shows all IoT devices as a numbered list.  The user can:
//   • Click checkboxes to toggle individual devices
//   • Type space-separated indices: "1 3 5 6"
//   • Use ranges: "1-3 5"
//   • Use commas: "1,3,5,6"
//   • "Select All" / "Select None" quick actions
//
// This mirrors PACDaemon's `ParseIndexes()` pattern from input_utils.cpp
// but in a PAC Glass dialog.  Controls which devices participate in
// telemetry streaming — cutting unnecessary mux-bin scope_keys.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import AppDialog from '@/shared/components/ui/AppDialog';
import PresenceDot from '@/shared/components/ui/PresenceDot';
import type { Device, DevicePresence } from '../../api/iotDevicesApi';
import type { TelemetryStreamersResult } from '../../hooks/useTelemetryStreamers';
import { parseIndexes } from '../../hooks/useTelemetryStreamers';
import { timeAgo } from '@/shared/telemetry';

/* ── Props ────────────────────────────────────────────────────────────── */

interface StreamerManagerDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  streamers: TelemetryStreamersResult;
  presenceList: DevicePresence[];
  palette: string[];
}

/* ── Readable device label ─────────────────────────────────────────────── */
function label(dev: Device): string {
  if (dev.label) return dev.label;
  const id = dev.device_id;
  return id.length > 8 ? id.slice(-8) : id;
}

/* ── Component ────────────────────────────────────────────────────────── */

export default function StreamerManagerDialog({
  open,
  onOpenChange,
  streamers,
  presenceList,
  palette,
}: StreamerManagerDialogProps) {
  const { allDevices, isEnabled, toggle, enableAll, disableAll, applyIndexSelection } = streamers;

  // Quick-select input (PACDaemon CLI-style)
  const [quickInput, setQuickInput] = useState('');
  const [quickError, setQuickError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Draft selection — Record<string, true> map (ES5-safe, no Set iteration)
  type DraftMap = Record<string, true>;
  const toDraft = (ids: string[]): DraftMap => {
    const m: DraftMap = {};
    for (const id of ids) m[id] = true;
    return m;
  };

  const [draft, setDraft] = useState<DraftMap>(() =>
    toDraft(allDevices.filter(d => isEnabled(d.device_id)).map(d => d.device_id)),
  );

  // Sync draft when dialog opens
  useEffect(() => {
    if (open) {
      setDraft(toDraft(
        allDevices.filter(d => isEnabled(d.device_id)).map(d => d.device_id),
      ));
      setQuickInput('');
      setQuickError(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDraft = useCallback((deviceId: string) => {
    setDraft(prev => {
      const next = { ...prev };
      if (next[deviceId]) delete next[deviceId];
      else next[deviceId] = true;
      return next;
    });
  }, []);

  const selectAllDraft = useCallback(() => {
    setDraft(toDraft(allDevices.map(d => d.device_id)));
  }, [allDevices]);

  const selectNoneDraft = useCallback(() => {
    setDraft({});
  }, []);

  // Apply PACDaemon-style index input to draft
  const applyQuickSelect = useCallback(() => {
    const trimmed = quickInput.trim();
    if (!trimmed) return;
    const result = parseIndexes(trimmed, allDevices.length);
    if (typeof result === 'string') {
      setQuickError(result);
      return;
    }
    setQuickError(null);
    const ids: string[] = result.map(idx => allDevices[idx - 1].device_id);
    setDraft(toDraft(ids));
  }, [quickInput, allDevices]);

  // Confirm: commit draft state to the hook
  const handleConfirm = useCallback(() => {
    streamers.setEnabled(Object.keys(draft));
    onOpenChange(false);
  }, [draft, streamers, onOpenChange]);

  // Presence lookup
  const presenceMap = useMemo(() => {
    const m = new Map<string, DevicePresence>();
    for (const p of presenceList) m.set(p.device_id, p);
    return m;
  }, [presenceList]);

  const draftCount = Object.keys(draft).length;
  const totalCount = allDevices.length;
  const allSelected = draftCount === totalCount;
  const noneSelected = draftCount === 0;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Telemetry Streamers"
      description={
        <span>
          Select which devices stream telemetry data.
          <span className="text-neutral-600 ml-1">
            {draftCount}/{totalCount} active
          </span>
        </span>
      }
      size="lg"
      onConfirm={handleConfirm}
      confirmLabel={`Apply (${draftCount} streamer${draftCount !== 1 ? 's' : ''})`}
      confirmDisabled={noneSelected}
    >
      <div className="flex flex-col gap-3">
        {/* ── Quick-select input (CLI-style) ────────────────────────── */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-neutral-400 tracking-wide uppercase">
            Quick Select — type indices separated by space
          </label>
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={quickInput}
              onChange={e => { setQuickInput(e.target.value); setQuickError(null); }}
              onKeyDown={e => { if (e.key === 'Enter') applyQuickSelect(); }}
              placeholder={`e.g. "1 3 5" or "1-${Math.min(3, totalCount)}" or "1,2,3"`}
              className="flex-1 px-3 py-1.5 rounded pac-glass-input text-sm font-mono
                text-neutral-100 placeholder:text-neutral-600
                focus:outline-none focus:ring-1 focus:ring-emerald-500/60"
            />
            <button
              onClick={applyQuickSelect}
              disabled={!quickInput.trim()}
              className="px-3 py-1.5 rounded text-xs font-medium tracking-wide
                pac-glass-btn border border-white/[0.06]
                hover:border-emerald-500/30 hover:text-emerald-400
                disabled:opacity-30 disabled:cursor-not-allowed
                transition-all duration-150"
            >
              Apply
            </button>
          </div>
          {quickError && (
            <span className="text-xs text-red-400">{quickError}</span>
          )}
        </div>

        {/* ── Bulk actions ──────────────────────────────────────────── */}
        <div className="flex items-center gap-2">
          <button
            onClick={selectAllDraft}
            disabled={allSelected}
            className="text-xs text-neutral-400 hover:text-emerald-400
              disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Select All
          </button>
          <span className="text-neutral-700">·</span>
          <button
            onClick={selectNoneDraft}
            disabled={noneSelected}
            className="text-xs text-neutral-400 hover:text-orange-400
              disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Select None
          </button>
          <div className="flex-1" />
          <span className="text-xs text-neutral-500 tabular-nums">
            {draftCount}/{totalCount} selected
          </span>
        </div>

        {/* ── Indexed device list ──────────────────────────────────── */}
        <div className="max-h-[40vh] overflow-y-auto rounded pac-glass border border-white/[0.04] divide-y divide-white/[0.04]">
          {allDevices.map((dev, idx) => {
            const index = idx + 1;
            const presence = presenceMap.get(dev.device_id);
            const online = presence?.online ?? false;
            const lastSeen = presence?.last_seen ?? dev.last_seen_at ?? null;
            const enabled = draft[dev.device_id] === true;
            const color = palette[idx % palette.length];

            return (
              <button
                key={dev.device_id}
                onClick={() => toggleDraft(dev.device_id)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left
                  transition-colors duration-100
                  ${enabled
                    ? 'hover:bg-white/[0.03]'
                    : 'opacity-40 hover:opacity-60 hover:bg-white/[0.02]'}`}
              >
                {/* Index number */}
                <span className="w-6 text-right text-xs font-mono text-neutral-500 shrink-0 tabular-nums">
                  {index}
                </span>

                {/* Checkbox */}
                <span
                  className={`w-4 h-4 rounded border shrink-0 flex items-center justify-center
                    transition-all duration-150
                    ${enabled
                      ? 'border-emerald-500/60 bg-emerald-500/20'
                      : 'border-neutral-600 bg-transparent'}`}
                >
                  {enabled && (
                    <svg className="w-2.5 h-2.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>

                {/* Online dot */}
                <PresenceDot online={online} className={online ? 'shadow-[0_0_4px_rgba(52,211,153,0.5)]' : ''} />

                {/* Palette swatch */}
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0 opacity-60"
                  style={{ backgroundColor: color }}
                />

                {/* Label */}
                <span className="text-sm text-neutral-200 truncate min-w-0">
                  {label(dev)}
                </span>

                {/* Device type badge */}
                {dev.device_type && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full
                    bg-white/[0.04] text-neutral-500 shrink-0 uppercase tracking-wider">
                    {dev.device_type}
                  </span>
                )}

                {/* Last seen */}
                <span className="ml-auto text-[10px] text-neutral-600 shrink-0 tabular-nums">
                  {lastSeen ? timeAgo(lastSeen) : '—'}
                </span>
              </button>
            );
          })}

          {allDevices.length === 0 && (
            <div className="px-4 py-6 text-center text-neutral-600 text-sm">
              No IoT devices found.
            </div>
          )}
        </div>

        {/* ── CLI hint ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 text-[10px] text-neutral-600 px-1">
          <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
          <span>
            Supports PACDaemon-style input: space-separated <code className="text-neutral-500">1 3 5</code>,
            ranges <code className="text-neutral-500">1-3</code>,
            commas <code className="text-neutral-500">1,2,3</code>,
            or mixed <code className="text-neutral-500">1 3-5 7</code>
          </span>
        </div>
      </div>
    </AppDialog>
  );
}
